import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Email, 
  Lock, 
  Person 
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { UserService } from '../pages/users/userService';
import { updateProfile } from 'firebase/auth';
import { THEME_COLORS, GRADIENTS, SHADOWS, BORDER_RADIUS } from '../../theme/themeConfig';
import ToastNotification from '../pages/common/Toastnotification';
import registerImage from '../../rawAssets/register.png';

// Toast notification state interface
interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Toast notification state
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
  });
  
  const { signup } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Helper function to show toast notifications
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };

  // Helper function to close toast
  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      showToast('Please fill in all fields', 'error');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      showToast('Passwords do not match', 'error');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      showToast('Password must be at least 6 characters', 'error');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Create the user account
      const userCredential = await signup(formData.email, formData.password);
      const user = userCredential.user;
      
      // Update the user's display name in Firebase Auth
      await updateProfile(user, {
        displayName: formData.name
      });
      
      // Create userClaims document in Firestore
      await UserService.createUserClaims(
        user.uid,
        formData.email,
        formData.name,
        undefined // No photo URL yet
      );
      
      showToast('Account created successfully! Redirecting...', 'success');
      
      // Navigate to dashboard after a short delay
      setTimeout(() => {
        navigate('/admin/login');
      }, 1000);
    } catch (error: any) {
      console.error('Registration error:', error);
      
      let errorMessage = 'Failed to create account';
      
      // Handle specific Firebase auth errors
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (error.message) {
        errorMessage = 'Failed to create account: ' + error.message;
      }
      
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const linkColor = isDark
    ? theme.palette.primary.dark
    : theme.palette.primary.main;

  const linkStyle: React.CSSProperties = {
    color: linkColor,
    textDecoration: 'none',
    fontWeight: 700,
  };

  // ── Decorative SVG accent shapes (mirrors Login's AccentShapes) ───────────
  const AccentShapes = () => (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      {/* Top-left large ring */}
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          left: -80,
          width: 260,
          height: 260,
          borderRadius: '50%',
          border: `2px solid ${
            isDark
              ? 'rgba(255,255,255,0.06)'
              : `${theme.palette.primary.main}18`
          }`,
        }}
      />
      {/* Top-left small ring */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          left: -20,
          width: 140,
          height: 140,
          borderRadius: '50%',
          border: `1.5px solid ${
            isDark
              ? 'rgba(255,255,255,0.04)'
              : `${theme.palette.primary.main}22`
          }`,
        }}
      />
      {/* Bottom-right blob */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -60,
          right: -60,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)'
            : `radial-gradient(circle, ${theme.palette.primary.main}0d 0%, transparent 70%)`,
        }}
      />
      {/* Tiny dot cluster top-right */}
      {[0, 1, 2].map((col) =>
        [0, 1, 2].map((row) => (
          <Box
            key={`dot-${col}-${row}`}
            sx={{
              position: 'absolute',
              top: 32 + row * 14,
              right: 40 + col * 14,
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: isDark
                ? 'rgba(255,255,255,0.08)'
                : `${theme.palette.primary.main}33`,
            }}
          />
        ))
      )}
      {/* Bottom-left horizontal line accent */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 52,
          left: 32,
          width: 56,
          height: 2,
          borderRadius: 1,
          background: isDark
            ? 'rgba(255,255,255,0.07)'
            : `${theme.palette.primary.main}30`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 60,
          left: 32,
          width: 32,
          height: 2,
          borderRadius: 1,
          background: isDark
            ? 'rgba(255,255,255,0.04)'
            : `${theme.palette.primary.main}1a`,
        }}
      />
    </Box>
  );

  // ── Mobile layout ─────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <Container
          component="main"
          maxWidth="sm"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 2,
            backgroundColor: 'background.default',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              width: '100%',
              borderRadius: BORDER_RADIUS.lg,
              backgroundColor: 'background.paper',
              boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : SHADOWS.lg,
              border: isDark ? `1px solid ${theme.palette.divider}` : 'none',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography
                component="h1"
                variant="h4"
                sx={{ mb: 4, fontWeight: 'bold', textAlign: 'center', color: 'text.primary' }}
              >
                Create an Account
              </Typography>

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="name"
                  label="Username"
                  name="name"
                  autoComplete="name"
                  autoFocus
                  value={formData.name}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 2, mb: 2, py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 'bold' }}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?{' '}
                    <Link to="/admin/login" style={linkStyle}>
                      Log In
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Container>

        <ToastNotification
          open={toast.open}
          message={toast.message}
          severity={toast.severity}
          onClose={handleCloseToast}
        />
      </>
    );
  }

  // ── Desktop layout ────────────────────────────────────────────────────────
  return (
    <>
      <Box sx={{ minHeight: '100vh', display: 'flex', backgroundColor: 'background.default' }}>
        <Grid container sx={{ minHeight: '100vh' }}>

          {/* ── Left side — Image panel (mirrors Login's right image panel) ── */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              backgroundImage: `url(${registerImage})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Multi-layer overlay for depth */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: isDark
                  ? 'linear-gradient(160deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.65) 100%)'
                  : 'linear-gradient(160deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.52) 100%)',
              }}
            />

            {/* Primary colour tint strip — right edge (mirrored from Login's left edge) */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 5,
                height: '100%',
                background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                opacity: 0.85,
              }}
            />

            {/* Top-left decorative circles (mirrored from Login's top-right) */}
            <Box
              sx={{
                position: 'absolute',
                top: -60,
                left: -60,
                width: 200,
                height: 200,
                borderRadius: '50%',
                border: '1.5px solid rgba(255,255,255,0.12)',
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: -10,
                left: -10,
                width: 110,
                height: 110,
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.08)',
                pointerEvents: 'none',
              }}
            />
          </Grid>

          {/* ── Right side — Form panel ────────────────────────────────────── */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.default',
              padding: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Decorative background shapes */}
            <AccentShapes />

            <Box
              sx={{
                width: '100%',
                maxWidth: 400,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                position: 'relative',
                zIndex: 1,
              }}
            >

              {/* ── Logo mark (identical to Login) ────────────── */}
              <Box
                sx={{
                  mb: 5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                {/* Geometric logo icon */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 14px ${theme.palette.primary.main}55`,
                    flexShrink: 0,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 2L17.32 6.5V15.5L10 20L2.68 15.5V6.5L10 2Z"
                      fill="white"
                      fillOpacity="0.9"
                    />
                    <path
                      d="M10 5.5L14.33 8V13L10 15.5L5.67 13V8L10 5.5Z"
                      fill="white"
                      fillOpacity="0.3"
                    />
                  </svg>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    letterSpacing: '-0.02em',
                    color: 'text.primary',
                    lineHeight: 1,
                  }}
                >
                  Admin Portal
                </Typography>
              </Box>

              {/* ── Heading block ─────────────────────────────── */}
              <Box sx={{ mb: 4, width: '100%' }}>
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    fontSize: '2rem',
                    letterSpacing: '-0.03em',
                    lineHeight: 1.15,
                    color: 'text.primary',
                    mb: 0.75,
                  }}
                >
                  Create an account
                </Typography>
                {/* Animated underline accent */}
                <Box
                  sx={{
                    height: 3,
                    width: 48,
                    borderRadius: 2,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    mb: 1.5,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                  }}
                >
                  Join the community and get started today
                </Typography>
              </Box>

              {/* ── Form ──────────────────────────────────────── */}
              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="name"
                  placeholder="Username"
                  name="name"
                  autoComplete="name"
                  autoFocus
                  value={formData.name}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2, '& .MuiInputLabel-root': { display: 'none' } }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  placeholder="Email"
                  name="email"
                  autoComplete="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  sx={{ mb: 2, '& .MuiInputLabel-root': { display: 'none' } }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2, '& .MuiInputLabel-root': { display: 'none' } }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1, '& .MuiInputLabel-root': { display: 'none' } }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 2, mb: 2, py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 'bold' }}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?{' '}
                    <Link to="/admin/login" style={linkStyle}>
                      Log In
                    </Link>
                  </Typography>
                </Box>
              </Box>

              {/* ── Footer note (identical to Login) ──────────── */}
              <Box
                sx={{
                  mt: 6,
                  pt: 3,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.success.main,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', fontSize: '0.75rem' }}
                >
                  Secured with end-to-end encryption
                </Typography>
              </Box>
            </Box>
          </Grid>

        </Grid>
      </Box>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default Register;